import { Box, Button, IconButton, Stack, Tooltip, Typography } from '@mui/material';

import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import UndoIcon from '@mui/icons-material/Undo';
import { useField } from 'formik';
import { memo, useMemo } from 'react';
import { toast } from 'react-toastify';

import QuestionForm from './QuestionForm';
import { QUESTION_TYPE_READABLE_STRINGS } from './QuestionTypeSelect';
import { remove } from 'utils/arrayHelpers';
import SurveyQuestionPromptPreview from './SurveyQuestionPromptPreview';
import { useQuestionExpand, useQuestionExpandState } from 'interviewEditor/QuestionExpandContext';
import { Question } from '@synergetx/puppet-interview-types';

type SurveyQuestionProps = {
    isLastQuestion: boolean;
    position: number;
    question: Question;
    surveyTab?: boolean;
};

const SurveyQuestion = ({ position }: SurveyQuestionProps) => {
    const [{ value: question }] = useField<Question>(`questions[${position}]`);
    const [, { error }] = useField<Question[] | undefined>(`questions`);
    const { isExpanded, toggle } = useQuestionExpandState(position);

    const handleToggle = () => {
        toggle();
        // Scroll to question when expanding (check before toggle since state hasn't updated yet)
        if (!isExpanded) {
            const questionClicked = document.getElementById(`interview_question_${question.name}`);
            if (questionClicked) {
                questionClicked.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }
    };

    // I had to undo some changes here to get the error text to render correctly the error text is set at the question level, not the question[position]
    const errorText = useMemo(() => {
        if (
            error &&
            error[position] &&
            Object.keys(error[position] as unknown as object).length > 0
        ) {
            return Object.keys(error[position]).map((errorField, i) => (
                <Typography key={errorField + i} variant="caption" color="error">
                    {[error[position][errorField as unknown as number]]}
                </Typography>
            ));
        } else {
            return null;
        }
    }, [error, position]);

    return (
        <Box
            onClick={isExpanded ? undefined : handleToggle}
            id={`interview_question_${question.name}`}
            sx={{
                padding: '1rem',
                paddingLeft: '3rem',
                marginBottom: '0rem',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'flex-start',
                alignItems: 'center',
                position: 'relative',
            }}
        >
            <li style={{ width: '100%', listStyleType: 'none' }}>
                {isExpanded ? (
                    <QuestionForm position={position} toggleEditing={handleToggle} />
                ) : (
                    <StaticQuestion position={position} question={question} />
                )}
                {errorText ? (
                    <Stack
                        sx={{
                            mt: 1,
                        }}
                    >
                        {errorText}
                    </Stack>
                ) : null}
            </li>
            <DeleteButton position={position} />
        </Box>
    );
};

const StaticQuestion = memo(({ position, question }: { position: number; question: Question }) => {
    return (
        <div>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginRight: '2rem',
                }}
            >
                <Typography
                    variant="h6"
                    sx={{
                        fontSize: 14,
                    }}
                >{`Question ${position + 1} - ${question.name}`}</Typography>
                <Typography
                    variant="h6"
                    sx={{
                        fontSize: 14,
                    }}
                >
                    {QUESTION_TYPE_READABLE_STRINGS[question.type]}
                </Typography>
            </Box>
            <SurveyQuestionPromptPreview question={question} />
        </div>
    );
});
StaticQuestion.displayName = 'StaticQuestion';

const UNDO_TIMEOUT_MS = 5000;

const DeleteButton = memo(({ position }: { position: number }) => {
    const [{ value: allQuestions }, , { setValue: setAllQuestions }] = useField<
        Question[] | undefined
    >(`questions`);
    const { expandedPositions, handleQuestionDeleted, handleQuestionRestored } =
        useQuestionExpand();

    const handleDelete = (e: React.MouseEvent) => {
        e.stopPropagation();

        // Capture state before deletion for potential undo
        const deletedQuestion = allQuestions?.[position];
        const wasExpanded = expandedPositions.has(position);

        if (!deletedQuestion) return;

        // Capture the full array for restoration before deletion
        const originalQuestions = [...(allQuestions || [])];

        // Perform the deletion
        setAllQuestions(remove(allQuestions || [], position));
        handleQuestionDeleted(position);

        // Show undo toast
        toast(
            ({ closeToast }) => (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                    }}
                >
                    <Typography variant="body2">Question deleted</Typography>
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<UndoIcon />}
                        onClick={() => {
                            // Restore to exact state before deletion
                            setAllQuestions(originalQuestions);
                            handleQuestionRestored(position, wasExpanded);
                            closeToast?.();
                        }}
                        sx={{ fontWeight: 'bold' }}
                    >
                        Undo
                    </Button>
                </Box>
            ),
            {
                autoClose: UNDO_TIMEOUT_MS,
                closeOnClick: false,
                pauseOnHover: true,
            },
        );
    };

    return (
        <Tooltip title="Delete this question?" placement="left-end">
            <IconButton
                sx={{ position: 'absolute', top: '0.1rem', right: '0.1rem' }}
                color="error"
                onClick={handleDelete}
            >
                <DeleteOutlineIcon />
            </IconButton>
        </Tooltip>
    );
});
DeleteButton.displayName = 'DeleteButton';

export default memo(SurveyQuestion);
